import { useObserva } from "@morganisbatman/observa-client-react";
import { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  Text,
  View,
} from "react-native";

import { fontFamily, space, textStyle } from "@passfirst/ui";

import { Button } from "../../components/button";
import { type Colors, useColors } from "../../theme";
import { restorePurchases } from "../../paywall/use-checkout-rail";
import { useManageSubscription } from "../use-manage-subscription";
import { Section } from "./section";

interface SubscriptionRow {
  purchaseId: string;
  verticalName: string;
  jurisdictionName: string | null;
}

interface Props {
  isLoading: boolean;
  subscriptions: SubscriptionRow[];
  onError: (msg: string | null) => void;
}

// Subscriptions surface. Always shows "Manage subscription" → Stripe
// billing portal when the user has at least one active sub. Empty
// state explains the one-off → lifetime model.
export function SubscriptionsSection({
  isLoading,
  subscriptions,
  onError,
}: Props): JSX.Element {
  const observa = useObserva();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const manage = useManageSubscription();
  const [restoring, setRestoring] = useState(false);
  const [restoreNotice, setRestoreNotice] = useState<string | null>(null);

  // Surface portal errors back up to the top-level Settings handler.
  useEffect(() => {
    onError(manage.error);
  }, [manage.error, onError]);

  async function handleRestore(): Promise<void> {
    setRestoring(true);
    setRestoreNotice(null);
    try {
      const result = await restorePurchases();
      // Receipts that failed server-side verification must be surfaced —
      // silently reporting "0 found" or a clean success when a purchase
      // couldn't be verified hides a real failure from the user (Apple
      // 3.1.1 expects Restore to behave honestly).
      if (result.errors.length > 0) {
        void observa.error({
          error: new Error(
            `restorePurchases: ${result.errors.length} verification failure(s)`,
          ),
          context: { restored: result.restored, errors: result.errors },
        });
      }
      if (result.restored === 0 && result.errors.length > 0) {
        setRestoreNotice(
          "We couldn't verify your purchase(s) — try again or contact support.",
        );
      } else if (result.restored === 0) {
        setRestoreNotice("No purchases found on this account.");
      } else if (result.errors.length > 0) {
        setRestoreNotice(
          `Restored ${result.restored} purchase${result.restored === 1 ? "" : "s"}, but some couldn't be verified — try again or contact support.`,
        );
      } else {
        setRestoreNotice(
          `Restored ${result.restored} purchase${result.restored === 1 ? "" : "s"}.`,
        );
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : "Restore failed");
    } finally {
      setRestoring(false);
    }
  }

  return (
    <Section title="Subscriptions">
      {isLoading ? (
        <ActivityIndicator />
      ) : subscriptions.length === 0 ? (
        <Text style={styles.empty}>
          No active subscriptions. Buying a one-off unlocks lifetime access
          for that vertical.
        </Text>
      ) : (
        subscriptions.map((e) => {
          const label = `${e.verticalName}${e.jurisdictionName ? ` — ${e.jurisdictionName}` : ""}`;
          return (
            <View key={e.purchaseId} style={styles.subRow}>
              <Text style={styles.subRowLabel}>{label}</Text>
              <Text style={styles.subRowMeta}>Recurring subscription</Text>
            </View>
          );
        })
      )}
      {subscriptions.length > 0 ? (
        <Button
          variant="secondary"
          fullWidth
          onPress={manage.openPortal}
          disabled={manage.busy}
          busy={manage.busy}
          label={manage.busy ? "Opening…" : "Manage subscription"}
          accessibilityLabel="Manage subscription"
          testID="settings-manage-subscription"
        />
      ) : null}

      {/* Restore Purchases — Apple-required on iOS; useful on Android.
          Hidden on web (Stripe purchases tie to a userId server-side,
          there's nothing for the client to restore). */}
      {Platform.OS !== "web" ? (
        <View style={{ gap: space[2] }}>
          <Button
            variant="secondary"
            fullWidth
            onPress={handleRestore}
            disabled={restoring}
            busy={restoring}
            label={restoring ? "Restoring…" : "Restore Purchases"}
            accessibilityLabel="Restore purchases"
            testID="settings-restore-purchases"
          />
          {restoreNotice ? (
            <Text style={styles.empty} accessibilityLiveRegion="polite">
              {restoreNotice}
            </Text>
          ) : null}
        </View>
      ) : null}
    </Section>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    empty: { ...textStyle.bodySmall, color: colors.textMuted },
    subRow: { gap: 2 },
    subRowLabel: {
      ...textStyle.body,
      color: colors.textPrimary,
      fontFamily: fontFamily.semiBold,
    },
    subRowMeta: { ...textStyle.caption, color: colors.textMuted },
  });
}
